import React, { useState, useEffect, ReactElement, useCallback } from 'react';
import styled from '@emotion/styled';
import { displayUserLocation, getOfferType, renderAvatar, tsSince } from '../../common';
import ConfirmationModal from '../modals/ConfirmationModal';
import Avatar from '../../atoms/Avatar';
import TextArea from '../../atoms/TextArea';
import OfferModal from './OfferModal';
import { vysionTrack } from '../../utils/vysion/vysionService';
import { useNavigate } from 'react-router-dom';
import { AccountMaker } from '../../models/Account';
import { ProfileMaker } from '../../models/Profile';
import OfferCounterOffer from '../../components/pages/Offers/OfferCounterOffer';
import { counterModalClose } from '../../actions/profileTrackAction';
import FavOfferCreate from '../../components/pages/Offers/FavOfferCreate';
import { OfferActions } from '../../utils/offers';
import { RootState } from '../../store';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { useDispatch, useSelector } from 'react-redux';
import {
  acceptOfferHighlight,
  counterOffer,
  ignoreOffer,
  refreshOffers,
  sendSuggestion,
} from '../../actions/offerActions';
import { getAccountByUserName, lessOffersCount } from '../../actions/profileActions';
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll';
import breakpoints from '../../theme/material/breakpoints';
import useMandatoryWall from '../../hooks/useMandatoryWall';
import { useCommonModal } from 'contexts/ModalContext';

const AvatarContainer = styled('div')({
  display: 'none',
  [breakpoints.up(480)]: {
    display: 'block',
    width: 96,
    height: 96,
    marginRight: 16,
    flexShrink: 0,
  },
});

interface OfferHighlightModalProps {
  profile: any;
  offer: any;
  containerId: string;
  onClose: () => void;
  isOpen: boolean;
  isFetching: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const OfferHighlightModal = ({
  profile,
  offer,
  containerId,
  onClose,
  isOpen,
  setIsOpen,
  isFetching,
}: OfferHighlightModalProps): ReactElement => {
  const dispatch = useDispatch();
  const errors = useSelector((state: RootState) => state.common.errors);
  const ownAccount = useSelector((state: RootState) => AccountMaker.create(state.profile));
  const ownProfile = useSelector((state: RootState) => ProfileMaker.create(state.profile.profile));

  useDisableBodyScroll(isOpen);

  const [acceptOfferModalOpen, setAcceptOfferModalOpen] = useState(false);
  const [counterOfferModalOpen, setCounterOfferModalOpen] = useState(false);
  const [dealMessage, setDealMessage] = useState('');
  const [hasOfferAction, setHasOfferAction] = useState(false);
  const [clickProfileAction, setClickProfileAction] = useState(false);
  const [showFavOfferCreateModal, setShowFavOfferCreateModal] = useState(false);
  const { showMandatoryWall } = useMandatoryWall();

  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();

  const { data: pData } = offer.other_account;
  const { data: profileData } = pData.profile;
  const avatar = renderAvatar(profileData.avatar.data.urls);
  const location = displayUserLocation(profile, profileData);
  const offerType = getOfferType(offer, profile);

  const photoCount = parseInt(profileData.approved_public_photos_count, 10);
  const privatePhotoCount = profileData.private_photos_count ? parseInt(profileData.private_photos_count, 10) : 0;

  const counterDisabled = offer.min_counter_price === offer.max_counter_price;
  const accountHashId = profileData.hash_id;

  const navigate = useNavigate();

  const handleVysionTrack = (action, component = '') => {
    vysionTrack({
      action,
      component,
      extraInfo: '',
      page: 'Forced action modal',
    });
  };

  const handleSuggestion = () => {
    dispatch(sendSuggestion(profileData.hash_id, false, null, null));
    setShowFavOfferCreateModal(false);
  };

  const acceptAttractive = (hashId, message) => {
    setAcceptOfferModalOpen(false);
    dispatch(acceptOfferHighlight(hashId, message));
  };

  const handleIgnore = () => {
    dispatch(ignoreOffer(offer.other_account.data.hash_id, true));
    dispatch(lessOffersCount());

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.OFFER_IGNORED,
      payload: {
        eventProperties: { Amount: offer.price_original, User: profile.hash_id },
      },
      otherAccountHashId: offer.initiator_hash_id,
    });
  };

  const getOfferDescription = () => {
    switch (offerType) {
      case 'suggestion_new':
        return 'Wants to go on a date!';
      case 'suggestion_pending':
        return 'Is considering a price';
      case 'offer_pending':
        return 'Is considering your offer';
      default:
        return null;
    }
  };

  const isProfileEligible = useCallback(
    (action: OfferActions) => {
      const isGenerous = ownProfile.isGenerous();
      const shouldShowTooManyLockedOffersDeals = ownAccount.shouldShowTooManyLockedOffersDeals(action);

      setIsOpen(false);

      if (isGenerous) {
        if (shouldShowTooManyLockedOffersDeals) {
          setShowTooManyPendingOfferModal(true);
          return false;
        }

        if (action === 'counter' && ownAccount.shouldShowTooManySentOffers()) {
          setShowTooManyPendingOfferModal(true);
          setModalArgs({
            type: 'sent',
          });

          return false;
        }
      }

      return true;
    },
    [ownProfile, ownAccount]
  );

  const handleIgnoreOffer = e => {
    e.preventDefault();

    if (isFetching) return;

    handleVysionTrack('click', 'ignore');

    setHasOfferAction(true);
    handleIgnore();
  };

  const handleCancelOffer = e => {
    e.preventDefault();

    if (isFetching) return;
    handleVysionTrack('click', 'cancelOffer');
  };

  const handleCancelSuggestion = e => {
    e.preventDefault();

    if (isFetching) return;
    handleVysionTrack('click', 'cancelSuggestion');

    handleIgnore();
  };

  const handleCounterOffer = formData => {
    return dispatch(counterOffer(formData, true, undefined));
  };

  const handleForceOpenCounterModal = () => {
    if (!isProfileEligible('counter')) {
      return;
    }

    showMandatoryWall(
      () => {
        if (isProfileEligible('counter')) {
          setIsOpen(true);
          setCounterOfferModalOpen(true);
        }
      },
      () => undefined,
      'Counter Offer'
    );
  };

  const handleSendOfferFromSuggestedOffer = () => {
    showMandatoryWall(
      () => {
        if (isProfileEligible('suggest')) {
          setIsOpen(true);
          setShowFavOfferCreateModal(true);
        }
      },
      () => undefined,
      'Make Offer'
    );
  };

  const handleCounter = e => {
    e.preventDefault();

    if (isFetching) return;

    handleVysionTrack('click', 'counter');
    handleForceOpenCounterModal();
  };

  const handleAcceptOffer = e => {
    e.preventDefault();

    if (isFetching || !isProfileEligible('accept')) return;

    handleVysionTrack('click', 'accept');

    showMandatoryWall(
      () => {
        setHasOfferAction(true);
        offerAccepted(accountHashId);
      },
      () => setAcceptOfferModalOpen(false),
      'Accept Offer'
    );
  };

  const handleOnClose = () => {
    handleVysionTrack('click', 'close');
    onClose();
  };

  const handleSuggestOffer = e => {
    e.preventDefault();

    if (isFetching) return;
    handleVysionTrack('click', 'counterSuggestOffer');
    handleSendOfferFromSuggestedOffer();
  };

  const offerAccepted = hashId => {
    ownProfile.isGenerous() ? dispatch(acceptOfferHighlight(hashId, null)) : setAcceptOfferModalOpen(true);
  };

  const currency = {
    initial: offer.currency_original.data.initial,
    label: offer.currency_original.data.label_unicode,
  };

  const offerDescription = getOfferDescription();

  useEffect(() => {
    if (clickProfileAction) {
      dispatch(getAccountByUserName(accountHashId)).then(response => {
        if (typeof response === 'undefined') {
          dispatch(refreshOffers('highlights', 'recent', true, 1));
          return;
        }

        if (!hasOfferAction) {
          navigate(`/profile/${accountHashId}`);
        }
      });
    }

    setHasOfferAction(false);
    setClickProfileAction(false);
  }, [hasOfferAction, clickProfileAction, accountHashId]);

  useEffect(() => {
    handleVysionTrack('view', 'display');
  }, []);

  return (
    <div id={containerId} data-test-id={containerId}>
      <OfferModal
        offer={offer}
        isOpen={isOpen}
        avatar={avatar}
        onClose={handleOnClose}
        setClickProfileAction={setClickProfileAction}
        username={pData.username}
        age={profileData.age}
        location={location}
        offerDescription={offerDescription}
        lastUpdated={tsSince(offer.updated_at.date)}
        currency={currency}
        price={offer.price_original}
        offerType={offerType}
        handleIgnoreOffer={handleIgnoreOffer}
        handleCounter={handleCounter}
        counterDisabled={counterDisabled}
        accountHashId={accountHashId}
        handleAcceptOffer={handleAcceptOffer}
        handleSuggestOffer={handleSuggestOffer}
        isGenerous={ownProfile.isGenerous()}
        handleCancelOffer={handleCancelOffer}
        handleCancelSuggestion={handleCancelSuggestion}
        photoCount={photoCount}
        privatePhotoCount={privatePhotoCount}
      />
      <ConfirmationModal
        data-test-id="accept-modal"
        contentLabel="Accept Offer Modal"
        isOpen={acceptOfferModalOpen}
        cancelText="Cancel"
        confirmText="Accept and Send"
        onClose={() => setAcceptOfferModalOpen(false)}
        onConfirm={() => acceptAttractive(profileData.hash_id, dealMessage)}
        buttonPosition="center"
        buttonID="acceptOfferHighlights"
        customStyle={{ overlay: { zIndex: 20000 } }}
      >
        <AvatarContainer style={{ margin: '0 auto', display: 'block', width: '96px' }}>
          <Avatar src={avatar} />
        </AvatarContainer>
        <p style={{ color: '#4396d7', fontWeight: 'bold', textAlign: 'center' }}>
          Send {pData.username} a message to accept their offer.
        </p>
        <TextArea
          rows={6}
          onChange={e => setDealMessage(e.target.value)}
          value={dealMessage}
          placeholder="Introduce yourself and tell them what you'd like to do on your first date!"
          resize="vertical"
          customInputStyle={{ fontSize: '16px' }}
        />
      </ConfirmationModal>
      <OfferCounterOffer
        isModalOpen={counterOfferModalOpen}
        avatarSrc={renderAvatar(profileData.avatar.data.urls, true)}
        offer={{
          data: offer,
        }}
        errors={errors}
        closeModal={() => {
          counterModalClose();
          setCounterOfferModalOpen(false);
        }}
        currentUserIsGenerous={ownProfile.isGenerous()}
        clearErrors={() => undefined}
        username={pData.username}
        counterOffer={handleCounterOffer}
        buttonID="counterOffer"
        successOpen={() => setIsOpen(false)}
        hashId={accountHashId}
      />
      <FavOfferCreate
        isModalOpen={showFavOfferCreateModal}
        avatarSrc={renderAvatar(profileData.avatar.data.urls, true)}
        username={pData.username}
        hashId={accountHashId}
        createNewOffer={() => undefined}
        successOpen={() => setIsOpen(false)}
        counterOffer={handleCounterOffer}
        isCounter={false}
        currencyInitial={currency.initial}
        currencyLabel={currency.label}
        currentUserIsGenerous={ownProfile.isGenerous()}
        sex={profile.sex}
        sendSuggestion={handleSuggestion}
        cannot_make_offer_reasons={profile.cannot_make_offer_reasons}
        buttonID="interestedOffers"
        isSuggestRequest={true}
        title="Make an Offer!"
        pageTitle={'Dashboard'}
        otherProfile={offer.other_account.data.profile.data}
        closeModal={() => {
          setShowFavOfferCreateModal(false);
        }}
      />
    </div>
  );
};

export default React.memo(OfferHighlightModal);
